import { z } from 'zod';
import { sfdcRecordUrl, gongEngageUrl } from './links';
import { signalInCommandCenter } from '../../../../../mini-apps/_shared/flow-links';
import { recordSignalId, SIGNAL_ID_KIND } from '@shared/schemas/terra-signals';

/**
 * EVIDENCE_LINK_REGISTRY — the evidence_ref scheme → deep-link/action mapper
 * for the contact-state "Terra Actions" strip (StateFactCard,
 * src/services/flow/contact-state-sources.ts adapters emit evidence_ref;
 * this file is the ONE client-side place that turns it into a URL or an
 * in-app action).
 *
 * evidence_ref is a stable "<scheme>:<subtype>:<id>" reference string, NOT a
 * URL (plan Decision 5, src/shared/schemas/flow-contact-state.ts file
 * header) — the resolver and its adapters never hardcode an instance URL or
 * app path. This registry is total over every scheme this file knows about;
 * an entry is either:
 *   - `{ kind: 'url', build }` — a real deep-link (StateFactCard renders an
 *     anchor), or
 *   - `{ kind: 'intent', intent }` — an in-app ACTION, not a link
 *     (StateFactCard renders a button that drives EnrollQueueContext).
 * An evidence_ref whose "<scheme>:<subtype>" isn't registered (a future
 * adapter not yet wired here, or a malformed ref) resolves to `{ kind: 'none' }`
 * — callers render the evidence_ref as plain text, never guess a URL for it
 * (no hardcoded fallback for an unmapped scheme).
 *
 * Registered schemes (2026-07-23 Spencer deep-link decisions, FINAL):
 *
 *  - `sfdc:contact:<id>`        → the SFDC Contact record.
 *  - `sfdc:signal_action:<id>`  → the Signals tab with that signal expanded
 *    (`signalInCommandCenter(recordSignalId('signal_action', id))`, plan
 *    2026-10-13-001 U3 — Action View was retired); a row outside the rep's
 *    current view is pinned (signalActionsSource).
 *  - `gong:contact:<id>`        → decision #1: the contact in GONG (Engage
 *    to-dos filtered by contact), NOT the SFDC record — supersedes the old
 *    `sfdc:contact` mapping for the Gong Flow fact specifically
 *    (gongFlowSource now emits `gong:contact:<id>`, not `sfdc:contact:<id>`).
 *    Built via `gongEngageUrl` (./links.tsx), the ONE core-owned copy of this
 *    URL shape — that file's own doc comment covers its (hand-synced)
 *    relationship to `mini-apps/action-view/utils.ts`'s twin of the same
 *    function; this registry just consumes it.
 *  - `launcher:campaign:<id>`   → U11 (plan 2026-09-24-005, Item G,
 *    supersedes decision #2's `/tools/launcher/<id>/manager` URL mapping):
 *    NOT a link anymore — opens the `CampaignMemberSidebar` in-page via the
 *    `campaign-member-open` intent (`useCampaignMemberSidebarStore`), scoped
 *    to the fact's campaignId + contactId. The Launcher MANAGER view is still
 *    reachable from inside that sidebar (its own "Open in Event Manager"
 *    escape hatch, marketing/admin only). Emitted by launcherInvitesSource
 *    for the campaign_member/invite_sent tiers only.
 *  - `flow:enroll-suggest:<campaignId>` → decision #3: NOT a link — the
 *    suggested-tier ("Suggested: …") fact is an ACTION, "Add to queue" with
 *    the campaign's play pre-assigned. Emitted by launcherInvitesSource ONLY
 *    for the `suggested` tier (tier 3, launcher_event_invitees) — tiers 1/2
 *    keep the `launcher:campaign` scheme above.
 *  - `flow:queue`               → decision #4: an intent that just opens the
 *    queue sidebar (EnrollQueueContext.open()), no params. Emitted by
 *    `enrollmentQueueSource` (contact-state-sources.ts) for every `queued`
 *    StateFact — reachable as of that adapter's ship (Feature 1). NOTE
 *    (U-GF-FE, 2026-07-23): StateFactCard now special-cases the `queued`
 *    dimension and never reads this entry when rendering that card (see its
 *    own doc comment) — this registration stays anyway, for any other
 *    surface that wants the open-sidebar action from a `flow:queue` ref.
 *  - `sfdc:signal_action_record:<id>` → U-GF-FE (Recent Task variant,
 *    2026-07-23): the raw SFDC Signal_Action__c record. Emitted by
 *    signalActionsSource ONLY for a CLOSED task (the "Recent Task" pill
 *    variant) — a closed fact's evidence_ref deliberately points at the SFDC
 *    record directly instead of the `sfdc:signal_action` / Signals-tab scheme
 *    above (which stays open-task-only).
 *  - `flow:request:<executionId>` → flow-workflow-requests U8 (Key
 *    Technical Decision #5): NOT a link — opens the EnrollQueue sidebar's
 *    Requests tab drilled straight into that execution's detail view (U6's
 *    `EnrollQueueContext.openRequestDetail(requestId)`). Emitted by
 *    `recentRequestSource` (contact-state-sources.ts) for every
 *    `recent_request` StateFact, `<executionId>` = the ActionExecution id.
 *    ALSO reused (2026-09-01 flow-contacts-bulk-send plan U4) by
 *    `recentQueueSendSource` for every `recent_queue_send` StateFact whose
 *    row carries an execution id — no new scheme was coined. A row with NO
 *    execution id emits the SAME scheme with an empty id segment
 *    (`flow:request:`), which the "NO id segment" guard below already
 *    resolves to `{ kind: 'none' }` (renders as plain text).
 *  - `flow:warm-intro:<warmIntroId>` → Warm Intro plan U6 (R8, mirrors the
 *    `flow:enroll-suggest` intent above): NOT a link — "Add to queue" with
 *    this connector's `salesforce_warm_intro.id` preselected via
 *    `QueueAddSchema.warm_intro_id` (add + preselect in one request, same as
 *    the enroll-suggest campaign preselect). Emitted by `warmIntroSource`
 *    (contact-state-sources.ts) for every `warm_intro` StateFact,
 *    `<warmIntroId>` = the `salesforce_warm_intro.id`.
 */

/**
 * Narrow read of the Gong Flow adapter's detail payload
 * (`GongFlowDetailSchema`, src/services/flow/contact-state-sources.ts) — only
 * the field this mapper needs. `detail` crosses the wire as `z.unknown()`
 * (StateFactSchema) so this IS a real trust-boundary parse (CLAUDE.md
 * "Validate at Trust Boundaries"), not decoration. Deliberately `.partial()`
 * and a DIFFERENT schema name from the backend's `GongFlowDetailSchema` (not
 * a duplicate of it — this reads one optional field, not the full shape), so
 * it never has to track that schema's other fields or their names.
 */
const GongDetailAccountIdSchema = z.object({ accountId: z.string().nullable() }).partial();

function gongDetailAccountId(detail: unknown): string | null {
  const parsed = GongDetailAccountIdSchema.safeParse(detail);
  return parsed.success ? parsed.data.accountId ?? null : null;
}

// ─────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────

export interface EvidenceLinkBuildContext {
  /** The StateFact's `detail` payload (adapter-defined, z.unknown() on the wire). */
  detail?: unknown;
}

interface UrlLinkEntry {
  kind: 'url';
  /**
   * User-facing anchor label for links built from this scheme ("where does
   * this open?"). Lives HERE, beside the URL builder, because the destination
   * is a property of the evidence scheme, not of the consuming surface — a
   * dimension-level label lies for mixed dimensions (e.g. `open_task` holds
   * both live tasks → the Signals tab and closed tasks → the raw SFDC record).
   */
  label: string;
  build: (id: string, ctx: EvidenceLinkBuildContext) => string;
}

interface IntentLinkEntry {
  kind: 'intent';
  intent: 'enroll-suggest' | 'queue-open' | 'requests-open' | 'warm-intro' | 'campaign-member-open';
}

type EvidenceLinkEntry = UrlLinkEntry | IntentLinkEntry;

const EVIDENCE_LINK_REGISTRY: Record<string, EvidenceLinkEntry> = {
  'sfdc:contact': { kind: 'url', label: 'Salesforce ↗', build: (id) => sfdcRecordUrl('Contact', id) },
  // Cross-app Terra links come from the flow-links registry (the deep-linking
  // standard's single source for cross-app URL shapes) — never hand-built here.
  'sfdc:signal_action': {
    kind: 'url',
    label: 'Signals ↗',
    build: (id) => signalInCommandCenter(recordSignalId(SIGNAL_ID_KIND.signal_action, id)),
  },
  'sfdc:signal_action_record': { kind: 'url', label: 'Salesforce ↗', build: (id) => sfdcRecordUrl('Signal_Action__c', id) },
  // U11 (plan 2026-09-24-005, Item G) — was `{ kind: 'url', build: campaignManagerInLauncher }`
  // (the Launcher MANAGER route); now an in-app intent so StateFactCard can
  // open the member sidebar in-page instead of navigating away. The
  // `campaignManagerInLauncher` deep-link itself still exists as the
  // sidebar's own "Open in Event Manager" escape hatch
  // (CampaignMemberSidebar.tsx), just no longer reachable from THIS registry.
  'launcher:campaign': { kind: 'intent', intent: 'campaign-member-open' },
  'gong:contact': { kind: 'url', label: 'Gong ↗', build: (id, ctx) => gongEngageUrl(id, gongDetailAccountId(ctx.detail)) },
  'flow:enroll-suggest': { kind: 'intent', intent: 'enroll-suggest' },
  'flow:queue': { kind: 'intent', intent: 'queue-open' },
  'flow:request': { kind: 'intent', intent: 'requests-open' },
  'flow:warm-intro': { kind: 'intent', intent: 'warm-intro' },
};

// ─────────────────────────────────────────────────────────────
// Resolution
// ─────────────────────────────────────────────────────────────

export type EvidenceLinkResult =
  | { kind: 'url'; url: string; label: string }
  | { kind: 'intent'; intent: 'enroll-suggest'; campaignId: string }
  | { kind: 'intent'; intent: 'queue-open' }
  | { kind: 'intent'; intent: 'requests-open'; requestId: string }
  | { kind: 'intent'; intent: 'warm-intro'; warmIntroId: string }
  | { kind: 'intent'; intent: 'campaign-member-open'; campaignId: string }
  | { kind: 'none' };

/**
 * Splits "<scheme>:<subtype>:<id...>" into a registry key ("<scheme>:<subtype>")
 * and the remaining id segment(s) (re-joined with ':', so an id containing a
 * colon round-trips). A ref with fewer than 2 ':'-parts (e.g. bare "sfdc") has
 * no subtype and can never match — returns null.
 */
function parseEvidenceRef(evidenceRef: string): { key: string; id: string } | null {
  const parts = evidenceRef.split(':');
  if (parts.length < 2) return null;
  const [scheme, subtype, ...rest] = parts;
  return { key: `${scheme}:${subtype}`, id: rest.join(':') };
}

/**
 * Resolve one evidence_ref (+ optional detail payload) to a URL, an in-app
 * intent, or `{ kind: 'none' }` (unrecognized scheme / malformed ref / a
 * url-kind scheme with no id segment) — StateFactCard renders plain text for
 * `none`.
 */
export function resolveEvidenceLink(evidenceRef: string, detail?: unknown): EvidenceLinkResult {
  const parsed = parseEvidenceRef(evidenceRef);
  if (!parsed) return { kind: 'none' };
  const entry = EVIDENCE_LINK_REGISTRY[parsed.key];
  if (!entry) return { kind: 'none' };

  if (entry.kind === 'url') {
    if (!parsed.id) return { kind: 'none' };
    return { kind: 'url', url: entry.build(parsed.id, { detail }), label: entry.label };
  }
  if (entry.intent === 'enroll-suggest') {
    // The campaign id IS the ref's id segment — StateFactCard maps it to a
    // live PlayOption via play_instance_ref.
    if (!parsed.id) return { kind: 'none' };
    return { kind: 'intent', intent: 'enroll-suggest', campaignId: parsed.id };
  }
  if (entry.intent === 'requests-open') {
    // The execution id IS the ref's id segment — StateFactCard passes it
    // straight to EnrollQueueContext.openRequestDetail(requestId).
    if (!parsed.id) return { kind: 'none' };
    return { kind: 'intent', intent: 'requests-open', requestId: parsed.id };
  }
  if (entry.intent === 'warm-intro') {
    // The salesforce_warm_intro id IS the ref's id segment — StateFactCard
    // passes it straight to addContact's warm_intro_id preselect (U6).
    if (!parsed.id) return { kind: 'none' };
    return { kind: 'intent', intent: 'warm-intro', warmIntroId: parsed.id };
  }
  if (entry.intent === 'campaign-member-open') {
    // The campaign id IS the ref's id segment — StateFactCard passes it
    // (plus the fact's own contact_key/observed_at) to
    // useCampaignMemberSidebarStore.open() (U11).
    if (!parsed.id) return { kind: 'none' };
    return { kind: 'intent', intent: 'campaign-member-open', campaignId: parsed.id };
  }
  // 'queue-open' — no id required (decision #4: no params).
  return { kind: 'intent', intent: 'queue-open' };
}

/**
 * Back-compat convenience wrapper for callers that only care about a URL
 * (e.g. the pre-registry test suite) — returns null for anything that isn't
 * a `{ kind: 'url' }` result (including intents; a caller that needs to
 * distinguish "no link" from "an action" must use `resolveEvidenceLink`
 * directly).
 */
export function mapEvidenceRefToUrl(evidenceRef: string, detail?: unknown): string | null {
  const result = resolveEvidenceLink(evidenceRef, detail);
  return result.kind === 'url' ? result.url : null;
}
